// DataStructurePage — the shared vocabulary forms are built from, across three
// tabs (Company field · Option sets · Topics). This file is the shell only:
// each tab is its own component in its own folder (company-fields/ over the
// company-field endpoints, option-sets/ over the form-sets ones, topics/ over
// the topic-sets ones), and a tab is MOUNTED only while active (the ternary
// below), so each tab's queries need no `skip` and its search/filters/selection
// reset on switch.
//
// The page owns two things on every tab's behalf. First the Add buttons: they
// live in the header beside the title, so each tab's create-modal open state is
// lifted here and handed down as `createOpen`/`onCloseCreate`. Second the count
// badges, fed by `lastTotals` (the ProjectsPage/PeoplePage idiom): each tab
// reports its own total through `onCount` when its list arrives UNFILTERED, so
// a badge shows the true entity total, appears once its tab has been opened,
// and survives tab switches even though the tab itself unmounts.

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tabs, type TabItem } from '@/components/common';
import useModuleAccess from '@/hooks/useModuleAccess';
import CompanyFieldsTab from './company-fields/CompanyFieldsTab';
import OptionSetsTab from './option-sets/OptionSetsTab';
import TopicsTab from './topics/TopicsTab';

type DataStructureTab = 'company-field' | 'option-sets' | 'topics';

const DataStructurePage = () => {
  const { t } = useTranslation();

  // Only `create` is needed here — it gates the three Add buttons. Each tab
  // reads its own verbs (the house idiom: per-component useModuleAccess calls
  // rather than drilling booleans).
  const { canCreate } = useModuleAccess('data_structure');

  const [tab, setTab] = useState<DataStructureTab>('company-field');
  const [createOpen, setCreateOpen] = useState(false);
  const [osCreateOpen, setOsCreateOpen] = useState(false);
  const [tpCreateOpen, setTpCreateOpen] = useState(false);

  // Tab badges — each entity's last-known UNFILTERED total. The tabs report
  // theirs; stable + no-op when unchanged, so none can loop.
  const [lastTotals, setLastTotals] = useState<{
    companyField?: number;
    optionSets?: number;
    topics?: number;
  }>({});
  const recordCfCount = useCallback(
    (n: number) => setLastTotals((c) => (c.companyField === n ? c : { ...c, companyField: n })),
    [],
  );
  const recordOsCount = useCallback(
    (n: number) => setLastTotals((c) => (c.optionSets === n ? c : { ...c, optionSets: n })),
    [],
  );
  const recordTpCount = useCallback(
    (n: number) => setLastTotals((c) => (c.topics === n ? c : { ...c, topics: n })),
    [],
  );

  const tabs: TabItem[] = [
    {
      value: 'company-field',
      label: t('data_structure_tab_company_field'),
      icon: 'building-2',
      count: lastTotals.companyField,
    },
    {
      value: 'option-sets',
      label: t('data_structure_tab_option_sets'),
      icon: 'layers',
      count: lastTotals.optionSets,
    },
    {
      value: 'topics',
      label: t('data_structure_tab_topics'),
      icon: 'bookmark',
      count: lastTotals.topics,
    },
  ];

  return (
    <div className='data-structure-page'>
      <header className='data-structure-page__header'>
        <div>
          <h1 className='data-structure-page__title'>{t('data_structure_title')}</h1>
          <p className='data-structure-page__subtitle'>{t('data_structure_subtitle')}</p>
        </div>
        {tab === 'company-field' && canCreate && (
          <Button iconStart='plus' onClick={() => setCreateOpen(true)}>
            {t('data_structure_cf_add')}
          </Button>
        )}
        {tab === 'option-sets' && canCreate && (
          <Button iconStart='plus' onClick={() => setOsCreateOpen(true)}>
            {t('data_structure_os_add')}
          </Button>
        )}
        {tab === 'topics' && canCreate && (
          <Button iconStart='plus' onClick={() => setTpCreateOpen(true)}>
            {t('data_structure_tp_add')}
          </Button>
        )}
      </header>

      <Tabs
        value={tab}
        onChange={(v) => setTab(v as DataStructureTab)}
        items={tabs}
        aria-label={t('data_structure_title')}
      />

      {tab === 'company-field' ? (
        <CompanyFieldsTab
          createOpen={createOpen}
          onCloseCreate={() => setCreateOpen(false)}
          onCount={recordCfCount}
        />
      ) : tab === 'option-sets' ? (
        <OptionSetsTab
          createOpen={osCreateOpen}
          onCloseCreate={() => setOsCreateOpen(false)}
          onCount={recordOsCount}
        />
      ) : (
        <TopicsTab
          createOpen={tpCreateOpen}
          onCloseCreate={() => setTpCreateOpen(false)}
          onCount={recordTpCount}
        />
      )}
    </div>
  );
};

export default DataStructurePage;
